import {
  Avatar,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { Visibility as ViewIcon } from '@mui/icons-material';
import { tokens, gradientForName } from '../../../theme/tokens';

const translations = {
  spanish: {
    expedient: 'Expediente', status: 'Estado', dates: 'Fechas', fees: 'Honorarios',
    draft: 'Borrador', initiated: 'Iniciado', processing: 'En trámite', completed: 'Finalizado',
    client: 'Cliente', lawyer: 'Abogado', partners: 'Colaboradores', notAssigned: 'No asignado',
    start: 'Inicio', end: 'Fin', created: 'Creado', progress: 'Progreso',
    completedCase: 'Completado', viewDetails: 'Ver',
  },
  english: {
    expedient: 'Case', status: 'Status', dates: 'Dates', fees: 'Fees',
    draft: 'Draft', initiated: 'Initiated', processing: 'In Progress', completed: 'Completed',
    client: 'Client', lawyer: 'Lawyer', partners: 'Partners', notAssigned: 'Not assigned',
    start: 'Start', end: 'End', created: 'Created', progress: 'Progress',
    completedCase: 'Completed', viewDetails: 'View',
  }
};

const currencyFormatter = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const getStatusColor = (state) => {
  switch (state) {
    case 'completed': return tokens.colors.ok;
    case 'processing': return tokens.colors.gold;
    case 'draft': return tokens.colors.muted2;
    default: return tokens.colors.indigo;
  }
};

const CaseTable = ({ expedients, onViewDetails, officeColor }) => {
  const tableLang = expedients[0]?.language || 'spanish';
  const tHeader = translations[tableLang] || translations.spanish;

  const getMemberName = (member, t) => {
    if (!member) return t.notAssigned;
    return member.person?.full_name?.replace(';', '') || member.name_str || t.notAssigned;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.replace(';', '').split(" ").filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: `${tokens.radius.lg}px`,
        border: `1px solid ${tokens.colors.line}`,
        boxShadow: tokens.shadow.card,
        backgroundColor: tokens.colors.panel,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { borderBottom: `1px solid ${tokens.colors.line}` } }}>
            {[tHeader.expedient, tHeader.client, tHeader.status, tHeader.fees, tHeader.progress, ''].map((label, i) => (
              <TableCell
                key={i}
                align={i === 5 ? 'right' : 'left'}
                sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', color: tokens.colors.muted2 }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {expedients.map((exp) => {
            const t = translations[exp.language] || translations.spanish;
            const statusColor = getStatusColor(exp.state);
            const progress = exp.tasks?.length
              ? {
                  percentage: Math.round((exp.tasks.filter(x => x.completed).length / exp.tasks.length) * 100),
                  completed: exp.tasks.filter(x => x.completed).length,
                  total: exp.tasks.length
                }
              : null;

            return (
              <TableRow
                key={exp.id}
                hover
                onClick={() => onViewDetails(exp.id)}
                sx={{
                  cursor: "pointer",
                  '& td': { borderBottom: `1px solid ${tokens.colors.line}` },
                  '&:hover': { backgroundColor: tokens.colors.panel2 },
                  '&:last-child td': { borderBottom: 'none' },
                }}
              >
                <TableCell>
                  <Typography sx={{ fontWeight: 640, fontSize: 14, color: tokens.colors.txt }}>{exp.title}</Typography>
                  <Typography sx={{ color: tokens.colors.muted, fontSize: 11.5, mt: 0.25 }}>
                    {exp.number} {exp.type && `• ${exp.type}`}
                  </Typography>
                </TableCell>

                <TableCell>
                  {exp.members?.client && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 26, height: 26, fontSize: 11, fontWeight: 700,
                          backgroundImage: gradientForName(getMemberName(exp.members.client, t)), color: '#0a0b10',
                        }}
                      >
                        {getInitials(getMemberName(exp.members.client, t))}
                      </Avatar>
                      <Typography sx={{ fontSize: 13, color: tokens.colors.muted }} noWrap>
                        {getMemberName(exp.members.client, t)}
                      </Typography>
                    </Box>
                  )}
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, fontWeight: 650, color: statusColor }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: statusColor }} />
                    {t[exp.state] || exp.state}
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography sx={{ fontSize: 13, fontWeight: 640, color: tokens.colors.txt }}>
                    {exp.billed_amount != null ? currencyFormatter.format(exp.billed_amount) : '—'}
                  </Typography>
                </TableCell>

                <TableCell sx={{ minWidth: 110 }}>
                  {progress && exp.state !== "completed" && (
                    <>
                      <Typography sx={{ fontSize: 11.5, color: tokens.colors.muted }}>
                        {progress.completed}/{progress.total} • {progress.percentage}%
                      </Typography>
                      <Box sx={{ height: 5, borderRadius: '20px', backgroundColor: tokens.colors.panel2, overflow: 'hidden', mt: 0.5 }}>
                        <Box sx={{ height: '100%', width: `${progress.percentage}%`, backgroundImage: tokens.gradient.main, borderRadius: '20px' }} />
                      </Box>
                    </>
                  )}
                  {exp.state === "completed" && progress && (
                    <Typography sx={{ fontSize: 11.5, color: tokens.colors.ok, fontWeight: 600 }}>
                      {t.completedCase}
                    </Typography>
                  )}
                </TableCell>

                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={(e) => { e.stopPropagation(); onViewDetails(exp.id); }}
                    sx={{
                      textTransform: 'none', fontWeight: 600, fontSize: 12.5,
                      color: tokens.colors.txt, borderRadius: `${tokens.radius.sm}px`,
                      '&:hover': { backgroundColor: tokens.colors.panel2 },
                    }}
                  >
                    {t.viewDetails}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CaseTable;